import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { of as RxOf } from 'rxjs/observable/of';
import { map } from 'rxjs/operators';
import { filter as _filter, includes as _includes } from 'lodash';
import { IContactExecuteItem, IContactExecuteResult, IContactLostReason, IContactDivSetting } from '@blcloud/bl-datamodel';
import { LogicalDeleteDiv } from '@blcloud/bl-datamodel/enum/common/logical-delete-div';
import { ContactDiv } from '@blcloud/bl-datamodel/enum/deals/contact-div';
import {
  ContactExecuteItemResource,
  ContactExecuteResultResource,
  ContactLostReasonResource,
  ContactDivSettingResource
} from '@blcloud/bl-ng-resource';
import { BlConditionType } from '@blcloud/bl-ng-common';
import { AUTO_GENERATION_HISTORY_EXECUTE_ITEM_CODE_LIST, CONTACT_METHOD_CODE_IGNORE } from './contact-page.interface';

/**
 * コンタクト系共通サービス
 */
@Injectable()
export class ContactService {

  /** コンタクト実施内容リストキャッシュ保持Map */
  private cacheContactExecuteItemListMap: Map<string, IContactExecuteItem[]> = new Map();

  /** コンタクト区分設定リストキャッシュ保持Map */
  private cacheContactDivSettingListMap: Map<string, IContactDivSetting[]> = new Map();

  /** コンタクト実施結果リストキャッシュ保持Map */
  private cacheContactExecuteResultListMap: Map<string, IContactExecuteResult[]> = new Map();

  /** コンタクト失注理由リストキャッシュ保持Map */
  private cacheContactLostReasonListMap: Map<string, IContactLostReason[]> = new Map();

  /**
   * コンストラクタ
   * @param contactExecuteItemResource コンタクト実施内容リソース
   * @param contactExecuteResultResource コンタクト実施結果リソース
   * @param contactLostReasonResource コンタクト失注理由リソース
   * @param contactLostReasonResource コンタクト区分設定リソース
   */
  constructor(
    private contactExecuteItemResource: ContactExecuteItemResource,
    private contactExecuteResultResource: ContactExecuteResultResource,
    private contactLostReasonResource: ContactLostReasonResource,
    private contactDivSettingResource: ContactDivSettingResource
  ) { }

  /**
   * コンタクト実施内容リストを検索して返します。
   * @param organizationCode 組織コード
   * @returns コンタクト実施内容リスト
   */
  searchContactExecuteItemList(organizationCode: string): Observable<IContactExecuteItem[]> {
    // キャッシュがあればそれを返却
    if (this.cacheContactExecuteItemListMap.has(organizationCode)) {
      return RxOf(this.cacheContactExecuteItemListMap.get(organizationCode));
    }

    // 検索条件
    const conditions = [
      { key: 'logicalDeleteDiv', value: LogicalDeleteDiv.Valid, type: BlConditionType.Equal },
      { key: 'organizationCode', value: organizationCode, type: BlConditionType.Equal }
    ];

    // 取得フィールド
    const fields = [
      'contactExecuteItemCode',
      'contactExecuteItem',
      'contactItem.contactCheckDiv'
    ];

    // ソート条件
    const sortFields = [
      { field: 'contactExecuteItemDispOrder', desc: false }
    ];

    return this.contactExecuteItemResource.getAll<IContactExecuteItem>({ conditions, fields, sortFields }).pipe(
      map(
        response => {
          const contactExecuteItemList = _filter(response.searchResultList, contactExecuteItem => {
            return !_includes(AUTO_GENERATION_HISTORY_EXECUTE_ITEM_CODE_LIST, contactExecuteItem.contactExecuteItemCode);
          });
          this.cacheContactExecuteItemListMap.set(organizationCode, contactExecuteItemList);
          return contactExecuteItemList;
        }
      )
    );
  }

  /**
   * コンタクト区分設定リストを検索して返します。
   * @param organizationCode 組織コード
   * @returns コンタクト区分設定リスト
   */
  searchContactDivSettingList(organizationCode: string): Observable<IContactDivSetting[]> {
    // キャッシュがあればそれを返却
    if (this.cacheContactDivSettingListMap.has(organizationCode)) {
      return RxOf(this.cacheContactDivSettingListMap.get(organizationCode));
    }

    // 検索条件
    const conditions = [
      { key: 'logicalDeleteDiv', value: LogicalDeleteDiv.Valid, type: BlConditionType.Equal },
      { key: 'organizationCode', value: organizationCode, type: BlConditionType.Equal },
      { key: 'contactDiv', value: ContactDiv.ContactMethod, type: BlConditionType.Equal },
      { key: 'contactDivDetailCode', value: CONTACT_METHOD_CODE_IGNORE, type: BlConditionType.NotEqual }
    ];

    // 取得フィールド
    const fields = [
      'contactDivDetailCode',
      'contactDivDetailCodeName'
    ];

    // ソート条件
    const sortFields = [
      { field: 'contactDivDetailDispOrder', desc: false }
    ];

    return this.contactDivSettingResource.getAll<IContactDivSetting>({ conditions, fields, sortFields }).pipe(
      map(
        response => {
          const contactDivSettingList = response.searchResultList;
          this.cacheContactDivSettingListMap.set(organizationCode, contactDivSettingList);
          return contactDivSettingList;
        }
      )
    );
  }

  /**
   * コンタクト実施結果リストを検索して返します。
   * @param organizationCode 組織コード
   * @returns コンタクト実施結果リスト
   */
  searchContactExecuteResultList(organizationCode: string): Observable<IContactExecuteResult[]> {
    // キャッシュがあればそれを返却
    if (this.cacheContactExecuteResultListMap.has(organizationCode)) {
      return RxOf(this.cacheContactExecuteResultListMap.get(organizationCode));
    }

    // 検索条件
    const conditions = [
      { key: 'logicalDeleteDiv', value: LogicalDeleteDiv.Valid, type: BlConditionType.Equal },
      { key: 'organizationCode', value: organizationCode, type: BlConditionType.Equal }
    ];

    // 取得フィールド
    const fields = [
      'logicalDeleteDiv',
      'organizationCode',
      'contactCreateDiv',
      'contactExecuteResultCode',
      'contactExecuteResult',
      'contactExecuteResultDiv'
    ];

    // ソート条件
    const sortFields = [
      { field: 'contactExecuteResultDispOrder', desc: false }
    ];

    return this.contactExecuteResultResource.getAll<IContactExecuteResult>({ conditions, fields, sortFields }).pipe(
      map(
        response => {
          this.cacheContactExecuteResultListMap.set(organizationCode, response.searchResultList);
          return response.searchResultList;
        }
      )
    );
  }

  /**
   * コンタクト失注理由リストを検索して返します。
   * @param organizationCode 組織コード
   * @returns コンタクト失注理由リスト
   */
  searchContactLostReasonList(organizationCode: string): Observable<IContactLostReason[]> {
    // キャッシュがあればそれを返却
    if (this.cacheContactLostReasonListMap.has(organizationCode)) {
      return RxOf(this.cacheContactLostReasonListMap.get(organizationCode));
    }

    const conditions = [
      { key: 'logicalDeleteDiv', type: BlConditionType.Equal, value: LogicalDeleteDiv.Valid },
      { key: 'organizationCode', value: organizationCode, type: BlConditionType.Equal }
    ];

    const fields = ['contactLostReasonCode', 'contactLostReason'];

    const sortFields = [
      { field: 'contactLostReasonDispOrder', desc: false }
    ];

    return this.contactLostReasonResource.getAll<IContactLostReason>({ conditions, fields, sortFields }).pipe(
      map(
        response => {
          this.cacheContactLostReasonListMap.set(organizationCode, response.searchResultList);
          return response.searchResultList;
        }
      )
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/contact/contact.service.ts